'use client';

import { addDays, format } from 'date-fns';
import { tz } from '@date-fns/tz';
import { ShippingDuration } from '../../model';

export interface ShippingDateRange {
  minDate: string;
  maxDate: string;
  minDateFormatted: string;
  maxDateFormatted: string;
}

/**
 * Calculates the estimated shipping date range based on the payment date and user's timezone.
 *
 * @param shippingDuration Shipping duration info (minDay, maxDay)
 * @param paymentDate Payment date (default: current date)
 * @param timeZone User's timezone (default: browser timezone)
 * @returns Estimated shipping date range
 */
export const calculateShippingDates = (
  shippingDuration: ShippingDuration,
  paymentDate: Date = new Date(),
  timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
): ShippingDateRange => {
  // Calculate min/max delivery dates (based on user timezone)
  const minDeliveryDate = addDays(paymentDate, shippingDuration.minDay);
  const maxDeliveryDate = addDays(paymentDate, shippingDuration.maxDay);

  // Format in user's timezone
  const minDateFormatted = format(minDeliveryDate, 'EEE, dd MMM', { in: tz(timeZone) });
  const maxDateFormatted = format(maxDeliveryDate, 'EEE, dd MMM', { in: tz(timeZone) });

  // Also provide as ISO strings (for use when needed)
  const minDate = format(minDeliveryDate, 'yyyy-MM-dd', { in: tz(timeZone) });
  const maxDate = format(maxDeliveryDate, 'yyyy-MM-dd', { in: tz(timeZone) });

  return {
    minDate,
    maxDate,
    minDateFormatted,
    maxDateFormatted,
  };
};

/**
 * Formats the shipping date range into a user-friendly string.
 *
 * @param shippingDateRange Shipping date range
 * @returns Formatted shipping date string (e.g., "Mon, 07 Jul - Wed, 09 Jul")
 */
export const formatShippingDateRange = (shippingDateRange: ShippingDateRange): string => {
  return `${shippingDateRange.minDateFormatted} - ${shippingDateRange.maxDateFormatted}`;
};
